/**
 * Hook pour vérifier les permissions de l'utilisateur
 * basé sur le système Types d'accès
 */

import { useState, useEffect, useCallback } from 'react';
import { apiGet } from '../utils/api';

// Cache des permissions pour éviter les appels répétés
let permissionsCache = null;
let cacheUserId = null;

/**
 * Hook pour récupérer et vérifier les permissions de l'utilisateur
 */
export const usePermissions = (tenantSlug, user) => {
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!tenantSlug || !user?.id) {
        setLoading(false);
        return;
      }

      // Utiliser le cache si disponible pour le même utilisateur
      if (permissionsCache && cacheUserId === user.id) {
        setPermissions(permissionsCache);
        setLoading(false);
        return;
      }

      try {
        // Pour admin, accès complet
        if (user.role === 'admin') {
          const fullAccess = { is_full_access: true };
          permissionsCache = fullAccess;
          cacheUserId = user.id;
          setPermissions(fullAccess);
          setLoading(false);
          return;
        }

        // Récupérer les permissions du type d'accès de l'utilisateur
        const response = await apiGet(tenantSlug, `/users/${user.id}/permissions`);
        permissionsCache = response.permissions || {};
        cacheUserId = user.id;
        setPermissions(permissionsCache);
      } catch (error) {
        console.error('Erreur chargement permissions:', error);
        // Fallback sur les permissions par défaut du rôle
        const fallback = getDefaultPermissionsForRole(user.role);
        setPermissions(fallback);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [tenantSlug, user?.id, user?.role]);

  /**
   * Vérifie si l'utilisateur a accès à un module
   */
  const hasModuleAccess = useCallback((moduleId) => {
    if (!permissions) return false;
    if (permissions.is_full_access) return true;
    return permissions.modules?.[moduleId]?.access === true;
  }, [permissions]);

  /**
   * Vérifie si l'utilisateur a une action spécifique sur un module
   */
  const hasModuleAction = useCallback((moduleId, action) => {
    if (!permissions) return false;
    if (permissions.is_full_access) return true;
    const modulePerms = permissions.modules?.[moduleId];
    if (!modulePerms?.access) return false;
    return modulePerms.actions?.includes(action) || false;
  }, [permissions]);

  /**
   * Vérifie si l'utilisateur a accès à un onglet
   */
  const hasTabAccess = useCallback((moduleId, tabId) => {
    if (!permissions) return false;
    if (permissions.is_full_access) return true;
    const modulePerms = permissions.modules?.[moduleId];
    if (!modulePerms?.access) return false;
    return modulePerms.tabs?.[tabId]?.access === true;
  }, [permissions]);

  /**
   * Vérifie si l'utilisateur a une action spécifique sur un onglet
   */
  const hasTabAction = useCallback((moduleId, tabId, action) => {
    if (!permissions) return false;
    if (permissions.is_full_access) return true;
    const modulePerms = permissions.modules?.[moduleId];
    if (!modulePerms?.access) return false;
    const tabPerms = modulePerms.tabs?.[tabId];
    if (!tabPerms?.access) return false;
    return tabPerms.actions?.includes(action) || false;
  }, [permissions]);

  /**
   * Vérifie si l'utilisateur peut signer les rapports d'intervention
   */
  const canSignInterventions = useCallback(() => {
    return hasTabAction('interventions', 'rapports', 'signer');
  }, [hasTabAction]);

  /**
   * Vérifie si l'utilisateur peut créer des rapports d'intervention
   */
  const canCreateInterventions = useCallback(() => {
    return hasTabAction('interventions', 'rapports', 'creer');
  }, [hasTabAction]);

  /**
   * Vérifie si l'utilisateur peut valider (DSI, etc.)
   */
  const canValidate = useCallback((moduleId, tabId) => {
    return hasTabAction(moduleId, tabId, 'valider');
  }, [hasTabAction]);

  /**
   * Visibilité d'un module dans le MENU : vrai si l'utilisateur a l'action 'voir' au
   * niveau du MODULE, OU sur au moins un de ses ONGLETS. Sans ça, un profil qui n'a
   * 'voir' que sur un onglet (ex. interventions → rapports-pr) verrait le module
   * disparaître du menu et ne pourrait plus atteindre cet onglet.
   */
  const canViewModule = useCallback((moduleId) => {
    if (!permissions) return false;
    if (permissions.is_full_access) return true;
    const modulePerms = permissions.modules?.[moduleId];
    if (!modulePerms?.access) return false;
    if (modulePerms.actions?.includes('voir')) return true;
    const tabs = modulePerms.tabs || {};
    return Object.values(tabs).some(t => t?.access && (t.actions || []).includes('voir'));
  }, [permissions]);

  return {
    permissions,
    loading,
    hasModuleAccess,
    hasModuleAction,
    hasTabAccess,
    hasTabAction,
    canSignInterventions,
    canCreateInterventions,
    canValidate,
    canViewModule
  };
};

/**
 * Permissions par défaut pour les rôles de base
 * (utilisé en fallback si l'API ne répond pas)
 */
const getDefaultPermissionsForRole = (role) => {
  if (role === 'admin') {
    return { is_full_access: true };
  }
  
  if (role === 'superviseur') {
    return {
      modules: {
        dashboard: { access: true, actions: ['voir'] },
        personnel: { access: true, actions: ['voir', 'creer', 'modifier', 'supprimer'] },
        // Onglet `requetes` aligné sur DEFAULT_PERMISSIONS['superviseur'].actifs du backend.
        actifs: {
          access: true,
          actions: ['voir', 'creer', 'modifier', 'supprimer'],
          tabs: { requetes: { access: true, actions: ['voir', 'creer', 'commenter', 'modifier'] } }
        },
        // Aligné sur DEFAULT_PERMISSIONS['superviseur']['modules']['batiments'] du backend.
        // Sans cette entrée, le moindre échec de GET /permissions (500, réseau, réveil de
        // la WebView mobile) fait disparaître Bâtiments du menu en silence.
        batiments: {
          access: true,
          actions: ['voir', 'creer', 'modifier', 'supprimer', 'exporter', 'exporter_historique'],
          tabs: {
            liste: { access: true, actions: ['voir', 'creer', 'modifier', 'supprimer', 'exporter', 'exporter_historique'] },
            carte: { access: true, actions: ['voir'] },
            import: { access: true, actions: ['creer'] }
          }
        },
        interventions: {
          access: true,
          // 'facturer' inclus : action de MODULE présente dans DEFAULT_PERMISSIONS
          // ['superviseur'] du backend (access_types.py). Sans elle, sur échec du
          // GET /permissions, hasModuleAction('interventions','facturer') renverrait
          // false et l'onglet Facturation disparaîtrait pour un superviseur.
          actions: ['voir', 'creer', 'modifier', 'supprimer', 'exporter', 'facturer'],
          tabs: {
            // 'supprimer' retiré : réservé admin (aligné sur DEFAULT_PERMISSIONS
            // ['superviseur'] du backend). Sinon, sur échec du GET /permissions,
            // ce repli afficherait le bouton corbeille à un superviseur → 403.
            rapports: { access: true, actions: ['voir', 'creer', 'creer_manuel', 'modifier', 'exporter', 'signer'] },
            'fausses-alarmes': { access: true, actions: ['voir', 'creer', 'modifier', 'supprimer', 'exporter'] },
            'conformite-dsi': { access: true, actions: ['voir', 'valider', 'exporter'] },
            historique: { access: true, actions: ['voir', 'exporter'] },
            parametres: { access: false, actions: [] }
          }
        },
        // Aligné sur DEFAULT_PERMISSIONS['superviseur']['modules']['paie'] du backend
        // (access_types.py). Sans la clé `tabs`, hasTabAccess('paie',X) renvoyait false
        // partout → sur échec du GET /permissions (500, réseau, réveil WebView mobile),
        // la barre d'onglets Paie du superviseur devenait VIDE. Même correctif que
        // batiments/interventions/documents ci-dessus.
        paie: {
          access: true,
          actions: ['voir', 'creer', 'modifier', 'supprimer', 'exporter'],
          tabs: {
            feuilles: { access: true, actions: ['voir', 'creer', 'modifier', 'supprimer', 'valider', 'exporter'] },
            rapports: { access: true, actions: ['voir', 'exporter'] },
            couts: { access: false, actions: [] },
            'jours-feries': { access: true, actions: ['voir', 'creer', 'modifier', 'supprimer', 'exporter'] },
            parametres: { access: false, actions: [] }
          }
        },
        // Onglets alignés sur DEFAULT_PERMISSIONS['superviseur'].planning du backend. Requis
        // depuis le scoping par onglet (M6 PR B) : le front gate en hasTabAction (sans repli) —
        // sans ces tabs, un échec de GET /permissions (réveil WebView) ferait perdre au
        // superviseur la gestion planning (calendrier/assignations/rapport-heures/export).
        planning: {
          access: true,
          actions: ['voir', 'creer', 'modifier', 'supprimer', 'exporter'],
          tabs: {
            calendrier: { access: true, actions: ['voir', 'creer', 'modifier', 'supprimer'] },
            assignations: { access: true, actions: ['voir', 'creer', 'modifier', 'supprimer'] },
            'equipe-jour': { access: true, actions: ['voir'] },
            'rapport-heures': { access: true, actions: ['voir', 'exporter'] },
            export: { access: true, actions: ['exporter'] },
            'audit-attribution': { access: true, actions: ['voir'] }
          }
        },
        // Aligné sur DEFAULT_PERMISSIONS['superviseur']['modules']['remplacements'] du
        // backend (access_types.py). Sans 'voir_tous' ni la clé `tabs`, hasModuleAction
        // ('remplacements','voir_tous') et hasTabAction('remplacements','conges'|'toutes-
        // demandes',...) renvoyaient false → sur échec du GET /permissions (500, réseau,
        // réveil WebView mobile), un superviseur perdait la vue gestionnaire (KPIs,
        // approbation des congés, approuver/refuser un quart). Depuis que ces gates sont
        // RBAC (PR #949) et non plus basés sur le rôle, ce repli DOIT porter les onglets.
        remplacements: {
          access: true,
          actions: ['voir', 'creer', 'modifier', 'supprimer', 'approuver', 'voir_tous', 'historique', 'accepter', 'refuser'],
          tabs: {
            propositions: { access: true, actions: ['voir', 'accepter', 'refuser'] },
            demandes: { access: true, actions: ['voir', 'creer', 'modifier', 'supprimer'] },
            conges: { access: true, actions: ['voir', 'creer', 'modifier', 'supprimer', 'approuver'] },
            echanges: { access: true, actions: ['voir', 'creer', 'voir_tous', 'accepter', 'refuser', 'approuver', 'historique'] },
            'toutes-demandes': { access: true, actions: ['voir', 'modifier', 'supprimer'] },
            parametres: { access: false, actions: [] }
          }
        },
        formations: { access: true, actions: ['voir', 'creer', 'modifier', 'supprimer'] },
        prevention: { access: true, actions: ['voir', 'creer', 'modifier', 'supprimer'] },
        // Onglets alignés sur DEFAULT_PERMISSIONS['superviseur'].disponibilites du backend.
        // Requis depuis le scoping par onglet de routes/disponibilites.py (M6) : le front
        // gate en hasTabAction (sans repli module) — sans ces tabs, un échec de GET
        // /permissions (réveil WebView) ferait perdre au superviseur la gestion d'équipe.
        disponibilites: {
          access: true,
          actions: ['voir', 'modifier'],
          tabs: {
            'mes-dispos': { access: true, actions: ['voir', 'modifier'] },
            equipe: { access: true, actions: ['voir', 'modifier'] },
            import: { access: true, actions: ['creer'] },
            rapport: { access: true, actions: ['voir', 'exporter'] }
          }
        },
        // Alignés sur DEFAULT_PERMISSIONS['superviseur'] du backend. Leur absence
        // faisait disparaître ces modules du menu au moindre échec de
        // GET /permissions (500, réseau, réveil de la WebView mobile).
        documents: {
          access: true,
          actions: ['voir', 'creer', 'modifier', 'supprimer', 'publier', 'relancer', 'exporter'],
          tabs: {
            'mes-lectures': { access: true, actions: [] },
            bibliotheque: { access: true, actions: ['voir'] },
            gestion: { access: true, actions: ['voir', 'creer', 'modifier', 'supprimer', 'publier'] },
            conformite: { access: true, actions: ['voir', 'relancer', 'exporter'] },
            parametres: { access: true, actions: ['voir', 'modifier'] },
          },
        },
        mesepi: { access: true, actions: ['voir'] },
        monprofil: { access: true, actions: ['voir', 'modifier'] },
        rapports: { access: false, actions: [] },
        parametres: { access: false, actions: [] }
      }
    };
  }
  
  // Employé par défaut - accès limité
  return {
    modules: {
      dashboard: { access: true, actions: ['voir'] },
      personnel: { access: false, actions: [] },
      // Onglet `requetes` aligné sur DEFAULT_PERMISSIONS['employe'].actifs : le pompier
      // signale un problème et le commente.
      actifs: {
        access: true,
        actions: ['voir'],
        tabs: { requetes: { access: true, actions: ['voir', 'creer', 'commenter'] } }
      },
      interventions: { access: false, actions: [], tabs: {} },
      paie: { access: false, actions: [] },
      // Onglets alignés sur DEFAULT_PERMISSIONS['employe'].planning du backend. Depuis le
      // scoping par onglet (M6 PR B) : correction de la typo 'assignation'→'assignations',
      // et 'rapport-heures' repassé à access:false (le backend le 403 déjà — ce repli
      // l'ouvrait à tort, correspondant à la fuite rapport-heures fermée côté serveur).
      planning: {
        access: true,
        actions: ['voir'],
        tabs: {
          calendrier: { access: true, actions: ['voir'] },
          assignations: { access: false, actions: [] },
          'equipe-jour': { access: true, actions: ['voir'] },
          'rapport-heures': { access: false, actions: [] },
          export: { access: false, actions: [] },
          'audit-attribution': { access: false, actions: [] }
        }
      },
      remplacements: { access: true, actions: ['voir', 'creer'] },
      formations: { access: true, actions: ['voir'] },
      // Le backend ACCORDE la prévention à l'employé
      // (DEFAULT_PERMISSIONS['employe']['modules']['prevention']). Ce repli
      // disait le contraire : au moindre échec de GET /permissions, un employé
      // perdait Prévention de son menu alors que le serveur la lui accorde.
      prevention: { access: true, actions: ['voir', 'creer', 'modifier'] },
      // Onglets alignés sur DEFAULT_PERMISSIONS['employe'].disponibilites du backend :
      // l'employé gère SES dispos (mes-dispos) mais n'a PAS l'équipe/import/rapport.
      // Depuis le scoping par onglet (M6), ceci referme aussi côté fallback l'écriture
      // des dispos d'autrui (equipe.access=false) que le module.modifier ouvrait à tort.
      disponibilites: {
        access: true,
        actions: ['voir', 'modifier'],
        tabs: {
          'mes-dispos': { access: true, actions: ['voir', 'modifier'] },
          equipe: { access: false, actions: [] },
          import: { access: false, actions: [] },
          rapport: { access: false, actions: [] }
        }
      },
      // Alignés sur le backend, comme ci-dessus.
      batiments: { access: false, actions: [] },
      documents: { access: true, actions: ['voir'] },
      mesepi: { access: true, actions: ['voir'] },
      monprofil: { access: true, actions: ['voir', 'modifier'] },
      rapports: { access: false, actions: [] },
      parametres: { access: false, actions: [] }
    }
  };
};

/**
 * Invalide le cache des permissions (à appeler après modification)
 */
export const invalidatePermissionsCache = () => {
  permissionsCache = null;
  cacheUserId = null;
};

export default usePermissions;
